import React from 'react';
import { Card } from '../../../../../components/Card/Card';
import { SortableList } from '../../../../../components/SortableList/SortableList';
import { sumByKey, formatCurrency } from '../../../../../util/util';
import { Event, loadEventStatistics, SaleStatistics } from '../../../../../model/event';
import { useRemoteData, useTask } from '../../../../../model/data';
import { uniq } from 'lodash';
import { t } from '../../../../../locales/translate';
import { runTicketsByPricingCategoryExport } from '../../../../../model/exports';
import { Icon } from '../../../../../components/Icon/Icon';

interface Props {
  event: Event;
}

function calculateTotals(stats: SaleStatistics[]): Array<{ label: string; count: number; total: number }> {
  const positive = stats.filter((item) => item.brutPrice > 0);
  const negative = stats.filter((item) => item.brutPrice <= 0);

  return [
    {
      label: `${t.model.sale.lessThanZeroEuro} (${formatCurrency(0)})`,
      count: sumByKey(negative, 'count'),
      total: sumByKey(negative, 'total'),
    },
    {
      label: `${t.model.sale.moreThanZeroEuro}`,
      count: sumByKey(positive, 'count'),
      total: sumByKey(positive, 'total'),
    },
  ];
}

export function SaleStats({ event }: Props): JSX.Element {
  const [stats, loading, error] = useRemoteData(
    async () => {
      await loadEventStatistics({ id: event.id });
    },
    (db) => db.table('events').find(event.id)?.pricingCategoryStatistics
  );
  const exportTask = useTask({ handleError: true });
  const categories = uniq(stats?.map((item) => item.pricingCategoryName) || []).sort();

  return (
    <Card
      title={t.model.sale.salesByPricingCategory}
      fullbleed
      actions={
        <Icon
          name="download--sm"
          tooltip={t.model.pricingCategory.csv}
          loading={exportTask.loading}
          onClick={() =>
            exportTask.run(
              runTicketsByPricingCategoryExport({
                eventId: event.id,
              })
            )
          }
        />
      }
    >
      {loading ? (
        <div>{t.actions.loading(t.model.sale.salesStatistics)}.</div>
      ) : error ? (
        <div>{t.errors.errorLoadingItem(t.model.sale.salesStatistics)}</div>
      ) : (
        categories.map((cat) => {
          return (
            <div className="pricing-stats-item" key={cat}>
              <header className="pricing-stats-item__header">{cat}</header>
              <SortableList
                rightSpacing="50%"
                items={stats?.filter((item) => item.pricingCategoryName === cat)}
                columns={[
                  {
                    dataKey: 'productVariantDescription',
                    label: t.model.variant._name,
                    width: 215,
                    cellRenderer: (row) => (
                      <>
                        {row.productVariantDescription}
                        {!row.physical ? ` (${t.model.ticket.onlineticket})` : ''}
                      </>
                    ),
                  },
                  {
                    dataKey: 'count',
                    label: t.model.item.quantity,
                    align: 'right',
                    width: 120,
                  },
                  {
                    dataKey: 'price',
                    label: t.model.price._name,
                    cellRenderer: (row) => formatCurrency(row.brutPrice),
                    align: 'right',
                    width: 120,
                  },
                  {
                    dataKey: 'total',
                    label: t.model.price.total,
                    cellRenderer: (row) => formatCurrency(row.total),
                    align: 'right',
                  },
                ]}
              ></SortableList>
            </div>
          );
        })
      )}
      {stats && (
        <div className="pricing-stats-item">
          <header className="pricing-stats-item__header">{t.model.sale.summary}</header>
          <SortableList
            rightSpacing="50%"
            items={calculateTotals(stats)}
            columns={[
              {
                dataKey: 'label',
                label: t.model.variant._name,
                width: 215,
              },
              {
                dataKey: 'count',
                label: t.model.item.quantity,
                align: 'right',
                width: 120,
              },
              {
                dataKey: 'total',
                label: t.model.price.total,
                cellRenderer: (row) => formatCurrency(row.total),
                align: 'right',
              },
            ]}
          ></SortableList>
        </div>
      )}
    </Card>
  );
}
